import Screen, { ScreenOpts } from './Screen';
import SceneManager from '../logic/SceneManager';
import { getCurrentScene } from '../../lib/stateUtils';
import StateObserver, { user } from 'src/StateObserver';
import { setHudVisibility } from 'src/state/ui';
import { DreamDriftHomePetView } from '../dreamDrift/components/DreamDriftHomePetView';
import { createEmitter } from 'src/lib/Emitter';
import { dreamDriftIsInstanceActive } from 'src/replicant/dreamDrift/dreamDrift.getters';
import { getPet, isPetConfigured } from '../pet';
import features from 'src/replicant/ruleset/features';

export default class PetScreen extends Screen {
  public readonly sceneManager: SceneManager;

  /** Temporary Dream Drift presentation shared by both supported home scenes. */
  private dreamDriftPet?: DreamDriftHomePetView;

  constructor(opts?: ScreenOpts) {
    super(opts);

    // create scene manager
    this.sceneManager = new SceneManager({ rootView: this });

    // Create the temporary presentation only for enabled players.
    if (features(user()).dreamDrift) {
      this.createDreamDriftPet();
    }
  }

  /** Create the Dream Drift home-pet presentation and its subscriptions. */
  private createDreamDriftPet(): void {
    // Keep one enabled presentation outside individual room implementations.
    this.dreamDriftPet = new DreamDriftHomePetView({
      zIndex: 1000,
      onActiveChanged: () => this.updateDreamDriftPetPresentation(),
    });

    // Reparent the presentation and update its mask as event or scene state changes.
    createEmitter(this, ({ ui, user }) => ({
      instance: user.dreamDrift?.instance,
      transition: ui.transition,
    })).addListener(() => this.updateDreamDriftPetPresentation());

    // Never leave the shared pet visually masked while the pet screen is detached.
    this.on('ViewRemoved', () => this.setRegularPetVisible(true));
  }

  public get currentScene() {
    const currentScene = getCurrentScene();

    return this.sceneManager.isValidScene(currentScene)
      ? this.sceneManager.getScene(currentScene)
      : undefined;
  }

  /** Load the temporary home presentation for enabled players. */
  public override async preloadScreen(): Promise<void> {
    // Load the bed only when the feature constructed its presentation.
    await this.dreamDriftPet?.preload();
  }

  public showScreen() {
    const showFooter = true;

    StateObserver.dispatch(
      setHudVisibility({
        header: true,
        footer: showFooter,
      }),
    );
  }

  /** Attach the temporary presentation to supported rooms and mask the real pet. */
  private updateDreamDriftPetPresentation(): void {
    // Disabled players never construct the temporary presentation.
    if (!this.dreamDriftPet) return;

    // Pet setup completes after the persistent pet screen is first constructed.
    if (!isPetConfigured()) return;

    // Derive activity and scene eligibility from the current authoritative state.
    const { ui, user } = StateObserver.getState();
    const currentTime = StateObserver.now();
    const scene = ui.transition.nextScene;
    const isHomeScene = scene === 'livingRoom' || scene === 'arena';
    const active = dreamDriftIsInstanceActive(user, currentTime);

    // Inactive and unsupported rooms use the unchanged regular-pet presentation.
    if (!isHomeScene || !active) {
      this.dreamDriftPet.removeFromSuperview();
      this.setRegularPetVisible(true);
      return;
    }

    // Keep the temporary view in the active room so it follows scene transitions.
    const sceneView = this.sceneManager.getScene(scene)!.getView();
    if (this.dreamDriftPet.getSuperview() !== sceneView) {
      sceneView.addSubview(this.dreamDriftPet);
    }

    // Mask the regular pet while the sleeping presentation owns interaction.
    this.setRegularPetVisible(false);
  }

  /** Set regular-pet visibility without changing room-owned lifecycle state. */
  private setRegularPetVisible(visible: boolean): void {
    // Pet setup may not have completed during the screen's first attachment.
    if (!isPetConfigured()) return;

    // Toggle rendering and input without changing room-owned pet visibility state.
    const entity = getPet().entity;
    entity.style.opacity = visible ? 1 : 0;
    entity.canHandleEvents(visible, !visible);
  }
}
